<template>
  <v-dialog v-model="aperto" width="720" scrollable @click:outside="chiudi">
    <template v-slot:activator="{ on }">
      <slot name="activator" :on="on">
        <v-btn outlined color="green" v-on="on">
          <v-icon left>mdi-plus</v-icon>Nuova
        </v-btn>
      </slot>
    </template>

    <v-card>
      <v-card-title class="grey--text text-subtitle-1">
        Nuova prenotazione crisantemi
      </v-card-title>

      <v-card-text>
        <v-row align="center" class="mt-1">
          <v-col cols="8">
            <v-autocomplete
              v-model="cliente"
              :items="clienti"
              item-text="nome"
              item-value="iva"
              label="Cliente"
              prepend-inner-icon="mdi-account"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="2" class="text-center">
            <span v-if="numero_cliente" class="text-h5 pink--text">
              {{ numero_cliente }}
            </span>
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="anno"
              :items="anni"
              label="Anno"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>

        <div v-if="verifica_in_corso" class="py-8 d-flex justify-center">
          <v-progress-circular indeterminate color="green" size="28" />
        </div>

        <!-- Esiste già: si apre invece di ricrearla -->
        <v-alert
          v-else-if="esistente"
          type="info"
          text
          class="mt-6 mb-0 d-flex align-center"
        >
          <v-row align="center" no-gutters>
            <v-col>
              Esiste già una prenotazione di questo cliente per il {{ anno }}.
            </v-col>
            <v-col cols="auto">
              <v-btn outlined color="blue" @click="apri(esistente)">
                <v-icon left>mdi-open-in-new</v-icon>Apri
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>

        <template v-else-if="cliente">
          <v-row class="mt-4">
            <v-col cols="6">
              <v-menu
                v-model="menu_data"
                :close-on-content-click="false"
                offset-y
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="data_leggibile"
                    label="Consegna presunta"
                    prepend-inner-icon="mdi-calendar"
                    outlined
                    dense
                    hide-details
                    readonly
                    clearable
                    v-on="on"
                    @click:clear="data_presunta = null"
                  />
                </template>
                <v-date-picker
                  v-model="data_presunta"
                  locale="it-it"
                  first-day-of-week="1"
                  :picker-date="mese_iniziale"
                  @input="menu_data = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <div class="mt-6 mb-2 grey--text text-caption">CONTENUTO</div>
          <v-item-group v-model="modalita" mandatory>
            <v-row>
              <v-col cols="6">
                <v-item v-slot="{ active, toggle }" value="vuota">
                  <v-card
                    outlined
                    :color="active ? 'green lighten-5' : undefined"
                    class="pa-4"
                    style="cursor: pointer"
                    @click="toggle"
                  >
                    <div class="d-flex align-center">
                      <v-icon :color="active ? 'green' : 'grey'" class="mr-3">
                        mdi-file-outline
                      </v-icon>
                      <div>
                        <div>Vuota</div>
                        <div class="text-caption grey--text">
                          Si parte da zero
                        </div>
                      </div>
                    </div>
                  </v-card>
                </v-item>
              </v-col>

              <v-col cols="6">
                <v-item v-slot="{ active, toggle }" value="clona">
                  <v-card
                    outlined
                    :color="active ? 'green lighten-5' : undefined"
                    class="pa-4"
                    style="cursor: pointer"
                    @click="toggle"
                  >
                    <div class="d-flex align-center">
                      <v-icon :color="active ? 'green' : 'grey'" class="mr-3">
                        mdi-content-copy
                      </v-icon>
                      <div class="flex-grow-1">
                        <div>Clona da un altro anno</div>
                        <div class="text-caption grey--text">
                          Copia prodotti, colori e note
                        </div>
                      </div>
                      <v-select
                        v-model="anno_origine"
                        :items="anni_origine"
                        dense
                        outlined
                        hide-details
                        style="max-width: 96px"
                        @click.stop
                      />
                    </div>
                  </v-card>
                </v-item>
              </v-col>
            </v-row>
          </v-item-group>

          <v-alert v-if="errore" type="error" text dense class="mt-4 mb-0">
            {{ errore }}
          </v-alert>
        </template>

        <div v-else class="py-10 text-center grey--text">
          Seleziona un cliente per continuare.
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text color="grey" @click="chiudi">Annulla</v-btn>
        <v-btn
          outlined
          color="green"
          :disabled="!cliente || !!esistente || verifica_in_corso"
          :loading="creazione_in_corso"
          @click="crea"
        >
          <v-icon left>mdi-check</v-icon>Crea e apri
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

/**
 * Creazione di una prenotazione crisantemi.
 *
 * La prenotazione nasce esplicitamente (vuota o clonata) e si apre subito nel
 * dettaglio: non c'è più una seconda griglia di inserimento, perché non serve
 * più scrivere colori prima che la prenotazione esista.
 *
 * Emette `apri` con la prenotazione, sia appena creata sia già esistente.
 */
export default {
  data() {
    return {
      aperto: false,
      cliente: null,
      anno: moment().format("YYYY"),
      data_presunta: null,
      menu_data: false,
      modalita: "vuota",
      anno_origine: String(Number(moment().format("YYYY")) - 1),
      esistente: null,
      verifica_in_corso: false,
      creazione_in_corso: false,
      errore: null,
    };
  },

  computed: {
    clienti() {
      return this.$store.getters.get_clienti;
    },

    numero_cliente() {
      if (!this.cliente) return null;
      const c = this.$store.getters.get_cliente_by_iva(this.cliente);
      return c ? c.numero : null;
    },

    anni() {
      const adesso = Number(moment().format("YYYY"));
      // Un anno avanti: le prenotazioni si aprono prima della campagna.
      return [...Array(8).keys()].map((i) => String(adesso + 1 - i));
    },

    anni_origine() {
      return this.anni.filter((a) => a !== this.anno);
    },

    data_leggibile() {
      return this.data_presunta
        ? moment(this.data_presunta).format("ddd D MMMM YYYY")
        : "";
    },

    // I crisantemi si consegnano attorno a Ognissanti: il calendario si apre
    // lì invece che sul mese corrente.
    mese_iniziale() {
      return `${this.anno}-10`;
    },
  },

  watch: {
    cliente: "verifica",
    anno(nuovo) {
      if (this.anno_origine === nuovo) {
        this.anno_origine = String(Number(nuovo) - 1);
      }
      this.verifica();
    },
  },

  methods: {
    async verifica() {
      this.errore = null;
      this.esistente = null;
      if (!this.cliente) return;
      this.verifica_in_corso = true;
      try {
        this.esistente = await this.$store.dispatch(
          "crisantemi/cerca_prenotazione",
          { cliente: this.cliente, anno: this.anno }
        );
      } catch {
        this.errore = "Non è stato possibile verificare le prenotazioni esistenti.";
      } finally {
        this.verifica_in_corso = false;
      }
    },

    async crea() {
      this.errore = null;
      this.creazione_in_corso = true;
      try {
        let prenotazione;

        if (this.modalita === "clona") {
          prenotazione = await this.$store.dispatch(
            "crisantemi/clona_prenotazione",
            {
              cliente: this.cliente,
              da_anno: this.anno_origine,
              a_anno: this.anno,
            }
          );
          if (!prenotazione) {
            this.errore = `Nessuna prenotazione da copiare per il ${this.anno_origine}.`;
            return;
          }
        } else {
          prenotazione = await this.$store.dispatch(
            "crisantemi/crea_prenotazione",
            { cliente: this.cliente, anno: this.anno }
          );
          // Creata da qualcun altro mentre compilavamo il dialog.
          if (prenotazione.gia_esistente) {
            this.esistente = prenotazione;
            return;
          }
        }

        if (this.data_presunta) {
          prenotazione = await this.$store.dispatch(
            "crisantemi/imposta_data_presunta",
            { id: prenotazione.id, ts: moment(this.data_presunta).format("X") }
          );
        }

        this.apri(prenotazione);
      } catch {
        this.errore = "Creazione non riuscita. Riprova.";
      } finally {
        this.creazione_in_corso = false;
      }
    },

    apri(prenotazione) {
      this.$emit("apri", prenotazione);
      this.chiudi();
    },

    chiudi() {
      this.aperto = false;
      this.cliente = null;
      this.data_presunta = null;
      this.esistente = null;
      this.errore = null;
      this.modalita = "vuota";
    },
  },
};
</script>
